import { useMemo } from 'react'

import { ModalContext } from '@domain-group/suit-theme'
import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditRelatedEntityModal from '@modals/CreateOrEditRelatedEntityModal'
import CreateOrEditTaskListModal from '@modals/CreateOrEditTaskListModal'
import CreateOrEditTaskListSectionModal from '@modals/CreateOrEditTaskListSectionModal'
import CreateOrEditTrackingLinkModal from '@modals/CreateOrEditTrackingLinkModal'
import UploadEntityAssetsModal from '@modals/UploadEntityAssetsModal'

import defaultRequestOptions from '@sections/Admin/packs/Projects/defaultRequestOptions'

import PageContent from './components/PageContent'

export type PageContextPayload = {
  callbacks: {
    deleteAttachment: (payload: HandleCallbackActionParams) => void,
    deleteRelatedEntity: (payload: HandleCallbackActionParams) => void,
    deleteTrackingLink: (payload: HandleCallbackActionParams) => void,
    showCreateOrEditRelatedEntityModal: (payload: object) => void,
    showCreateOrEditTaskListSectionModal: (payload: object) => void,
    showCreateOrEditTrackingLinkModal: (payload: object) => void,
    showUploadEntityAssetsModal: (payload: object) => void,
  },
}

const callbacks = (component: string, setState) => {
  const componentCallbacks = {
    CreateOrEditRelatedEntityModal: {
      closeModal: () => setState({ showCreateOrEditRelatedEntityModal: false }),
      createRelatedEntity: payload => handleCallbackAction(payload),
      updateRelatedEntity: payload => handleCallbackAction(payload),
    },
    CreateOrEditTaskListModal: {
      closeModal: () => setState({ showCreateOrEditTaskListModal: false }),
      toggleTaskListCompletion: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateTaskList: payload => handleCallbackAction(payload),
    },
    CreateOrEditTaskListSectionModal: {
      closeModal: () => setState({ showCreateOrEditTaskListSectionModal: false }),
      createTaskListSection: payload => handleCallbackAction(payload),
      updateTaskListSection: payload => handleCallbackAction(payload),
    },
    CreateOrEditTrackingLinkModal: {
      closeModal: () => setState({ showCreateOrEditTrackingLinkModal: false }),
      createTrackingLink: payload => handleCallbackAction(payload),
      updateTrackingLink: payload => handleCallbackAction(payload),
    },
    UploadEntityAssetsModal: {
      closeModal: () => setState({ showUploadEntityAssetsModal: false }),
      deleteAttachment: payload => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState = {
  showCreateOrEditRelatedEntityModal: false,
  showCreateOrEditTaskListModal: false,
  showCreateOrEditTaskListSectionModal: false,
  showCreateOrEditTrackingLinkModal: false,
  showUploadEntityAssetsModal: false,
}

const Overview = () => {
  const { taskListId } = useNumericParams()

  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrEditRelatedEntityModal,
    showCreateOrEditTaskListModal,
    showCreateOrEditTaskListSectionModal,
    showCreateOrEditTrackingLinkModal,
    showUploadEntityAssetsModal,
  } = state

  const modalContext = useModals()
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      deleteAttachment: payload => handleCallbackAction(payload),
      deleteRelatedEntity: payload => handleCallbackAction(payload),
      deleteTrackingLink: payload => handleCallbackAction(payload),
      showCreateOrEditRelatedEntityModal: (payload) => {
        setModalData('CreateOrEditRelatedEntityModal', payload)
        setState({ showCreateOrEditRelatedEntityModal: true })
      },
      showCreateOrEditTaskListModal: (payload: object) => {
        setModalData('CreateOrEditTaskListModal', payload)
        setState({ showCreateOrEditTaskListModal: true })
      },
      showCreateOrEditTaskListSectionModal: (payload: object) => {
        setModalData('CreateOrEditTaskListSectionModal', payload)
        setState({ showCreateOrEditTaskListSectionModal: true })
      },
      showCreateOrEditTrackingLinkModal: (payload: object) => {
        setModalData('CreateOrEditTrackingLinkModal', payload)
        setState({ showCreateOrEditTrackingLinkModal: true })
      },
      showUploadEntityAssetsModal: (payload: object) => {
        setModalData('UploadEntityAssetsModal', payload)
        setState({ showUploadEntityAssetsModal: true })
      },
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent taskListId={taskListId} />

        <CreateOrEditRelatedEntityModal
          callbacks={callbacks('CreateOrEditRelatedEntityModal', setState)}
          showModal={showCreateOrEditRelatedEntityModal}
        />

        <CreateOrEditTaskListModal
          callbacks={callbacks('CreateOrEditTaskListModal', setState)}
          requestOptions={{
            ...defaultRequestOptions.taskList,
            include_related_entity_subject: true,
            include_task_list_related_entities: true,
            include_task_list_sections: true,
            include_task_list_task_counts: true,
          }}
          showModal={showCreateOrEditTaskListModal}
        />

        <CreateOrEditTaskListSectionModal
          callbacks={callbacks('CreateOrEditTaskListSectionModal', setState)}
          showModal={showCreateOrEditTaskListSectionModal}
        />

        <CreateOrEditTrackingLinkModal
          callbacks={callbacks('CreateOrEditTrackingLinkModal', setState)}
          showModal={showCreateOrEditTrackingLinkModal}
        />

        <UploadEntityAssetsModal
          callbacks={callbacks('UploadEntityAssetsModal', setState)}
          showModal={showUploadEntityAssetsModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Overview
